/**
 * 토큰 관리를 중앙화하는 클래스
 * 모든 토큰 관련 로직을 여기서 처리
 * 주기적 토큰 갱신 및 자동 관리 포함
 */

import { TOKEN_KEY, TOKEN_TYPE_KEY, REFRESH_TOKEN_KEY, USER_KEY, ID_TOKEN_KEY } from './types';
import type { StorageConfig } from './types/auth-config';
import { DEFAULT_STORAGE_KEYS, resolveStorageKeys } from './storage-keys';
import { handleRefreshTokenFlow } from './utils/refresh';
import { API_BASE_URLS } from '@edusense/shared-constants';

export class TokenManager {
  private static instance: TokenManager;
  private currentToken: string | null = null;
  private currentRefreshToken: string | null = null;
  private currentTokenType: string = 'Bearer';
  private currentExpiresAt: number | null = null;
  private listeners: Array<(token: string | null) => void> = [];
  private tokenSetCallback: ((token: string | null, tokenType?: string) => void) | null = null;

  // 주기적 갱신 관련 속성
  private refreshTimer: NodeJS.Timeout | null = null;
  // 중요: authServerUrl은 항상 auth.ugot.uk를 사용해야 함
  private authServerUrl: string = API_BASE_URLS.AUTH_SERVER; // 'https://auth.ugot.uk'
  private isRefreshing: boolean = false;
  private pendingRefreshPromise: Promise<boolean> | null = null;
  private storageKeys = DEFAULT_STORAGE_KEYS;

  private constructor() {
    // ✅ Lazy Loading: localStorage 읽기를 생성자에서 제거
    // E2E 테스트 환경에서 Playwright storageState가 로드된 후에 토큰을 읽도록 함
    // getToken(), getRefreshToken() 등이 호출될 때 localStorage를 읽음
  }

  static getInstance(): TokenManager {
    if (!TokenManager.instance) {
      TokenManager.instance = new TokenManager();
    }
    return TokenManager.instance;
  }

  configureStorage(storage?: StorageConfig): void {
    this.storageKeys = resolveStorageKeys(storage);

    if (typeof localStorage === 'undefined') {
      return;
    }

    this.currentToken = localStorage.getItem(this.storageKeys.tokenKey);
    this.currentRefreshToken = localStorage.getItem(this.storageKeys.refreshTokenKey);
    this.currentTokenType = localStorage.getItem(this.storageKeys.tokenTypeKey) || 'Bearer';

    const expiresAt = localStorage.getItem(this.storageKeys.tokenExpiresAtKey);
    this.currentExpiresAt = expiresAt ? parseInt(expiresAt, 10) : null;
  }

  /**
   * 외부 API 토큰 설정 콜백 등록
   */
  setTokenSetCallback(callback: (token: string | null, tokenType?: string) => void): void {
    this.tokenSetCallback = callback;
    // 기존 토큰이 있으면 즉시 호출
    if (this.currentToken) {
      callback(this.currentToken);
    }
  }

  /**
   * 토큰 설정 (동기적으로 처리)
   */
  setToken(token: string | null, tokenType: string = 'Bearer'): void {
    console.log('[TokenManager] Setting token:', token ? '<token exists>' : 'null');
    
    const previousToken = this.currentToken;
    this.currentToken = token;
    this.currentTokenType = tokenType || 'Bearer';

    if (token) {
      localStorage.setItem(this.storageKeys.tokenKey, token);
      if (tokenType) {
        localStorage.setItem(this.storageKeys.tokenTypeKey, tokenType);
      }
    } else {
      localStorage.removeItem(this.storageKeys.tokenKey);
      localStorage.removeItem(this.storageKeys.tokenTypeKey);
    }

    // Clean up legacy token keys to avoid drift
    localStorage.removeItem('auth_access_token');
    localStorage.removeItem('accessToken');

    // 외부 API 서비스에 토큰 설정
    if (this.tokenSetCallback) {
      this.tokenSetCallback(token, tokenType);
    }

    // 토큰이 변경된 경우에만 리스너에게 알림
    if (previousToken !== token) {
      this.notifyListeners(token);
    }
  }

  /**
   * 리프레시 토큰 설정
   */
  setRefreshToken(refreshToken: string | null): void {
    console.log('[TokenManager] Setting refresh token:', refreshToken ? '<refresh token exists>' : 'null');
    
    this.currentRefreshToken = refreshToken;

    if (refreshToken) {
      localStorage.setItem(this.storageKeys.refreshTokenKey, refreshToken);
    } else {
      localStorage.removeItem(this.storageKeys.refreshTokenKey);
    }
  }

  /**
   * 현재 토큰 반환 (Lazy Loading)
   * E2E 테스트 호환성: 항상 localStorage를 먼저 확인하여 최신 값 반환
   */
  getToken(): string | null {
    // ✅ Lazy Loading: 항상 localStorage를 먼저 확인
    // Playwright storageState가 로드된 후에도 정상 작동
    if (typeof localStorage !== 'undefined') {
      const storedToken = localStorage.getItem(this.storageKeys.tokenKey);

      // 🔍 디버깅: localStorage 값 확인
      console.log('[TokenManager.getToken] 🔍', {
        storedToken: storedToken ? `exists (${storedToken.length} chars)` : 'null',
        cachedToken: this.currentToken ? `exists (${this.currentToken.length} chars)` : 'null',
        needsSync: storedToken !== this.currentToken
      });

      // 캐시와 localStorage가 다르면 동기화
      if (storedToken !== this.currentToken) {
        this.currentToken = storedToken;
        if (storedToken) {
          console.log('[TokenManager] ✅ Token synced from localStorage');
        }
      }
    }
    return this.currentToken;
  }

  /**
   * 현재 리프레시 토큰 반환 (Lazy Loading)
   */
  getRefreshToken(): string | null {
    // ✅ Lazy Loading: localStorage에서 최신 값 읽기
    if (typeof localStorage !== 'undefined') {
      const storedRefreshToken = localStorage.getItem(this.storageKeys.refreshTokenKey);
      if (storedRefreshToken !== this.currentRefreshToken) {
        this.currentRefreshToken = storedRefreshToken;
      }
    }
    return this.currentRefreshToken;
  }

  /**
   * 현재 토큰 타입 반환 (Lazy Loading)
   */
  getTokenType(): string {
    // ✅ Lazy Loading: localStorage에서 최신 값 읽기
    if (typeof localStorage !== 'undefined') {
      const storedTokenType = localStorage.getItem(this.storageKeys.tokenTypeKey);
      if (storedTokenType && storedTokenType !== this.currentTokenType) {
        this.currentTokenType = storedTokenType;
      }
    }
    return this.currentTokenType;
  }

  /**
   * 토큰 타입 설정
   */
  setTokenType(tokenType: string): void {
    this.currentTokenType = tokenType;
    localStorage.setItem(this.storageKeys.tokenTypeKey, tokenType);
  }

  /**
   * 토큰 만료 시간 설정 (expiresIn: 초 단위)
   */
  setExpiresIn(expiresIn: number): void {
    // 현재 시간 + expiresIn 초로 만료 시간 계산
    this.currentExpiresAt = Date.now() + (expiresIn * 1000);
    localStorage.setItem(this.storageKeys.tokenExpiresAtKey, this.currentExpiresAt.toString());
  }

  /**
   * 토큰 만료 여부 확인 (Lazy Loading)
   */
  isTokenExpired(): boolean {
    // ✅ Lazy Loading: localStorage에서 만료 시간 읽기
    if (typeof localStorage !== 'undefined') {
      const expiresAtStr = localStorage.getItem(this.storageKeys.tokenExpiresAtKey);
      if (expiresAtStr) {
        this.currentExpiresAt = parseInt(expiresAtStr, 10);
      }
    }

    if (!this.currentExpiresAt) {
      return false; // 만료 시간이 없으면 만료되지 않은 것으로 가정
    }
    return Date.now() >= this.currentExpiresAt;
  }

  /**
   * 토큰 존재 여부 확인 (Lazy Loading)
   */
  hasToken(): boolean {
    // ✅ getToken()을 호출하여 최신 localStorage 값 확인
    return !!this.getToken();
  }

  /**
   * 저장된 사용자 정보를 반환 (없으면 null)
   */
  getUser<T = any>(): T | null {
    if (typeof localStorage === 'undefined') {
      return null;
    }

    const storedUserJson = localStorage.getItem(this.storageKeys.userKey);
    if (!storedUserJson) {
      return null;
    }

    try {
      return JSON.parse(storedUserJson) as T;
    } catch (error) {
      console.warn('[TokenManager] Failed to parse stored user JSON:', error);
      return null;
    }
  }

  /**
   * 사용자 ID를 반환 (여러 필드 중 존재하는 값을 탐색)
   */
  getUserId(): string | null {
    const user = this.getUser<any>();
    if (!user) {
      return null;
    }

    const candidates = [
      user.id,
      user.userId,
      user.userID,
      user.uid,
      user.sub,
      user?.profile?.id,
      user?.profile?.userId,
    ].filter(Boolean);

    if (candidates.length > 0) {
      return String(candidates[0]);
    }

    return null;
  }

  /**
   * 사용자 표시 이름 반환 (없으면 null)
   */
  getUserDisplayName(): string | null {
    const user = this.getUser<any>();
    if (!user) {
      return null;
    }

    const candidates = [
      user.displayName,
      user.name,
      user.fullName,
      user.username,
      user.userName,
      user?.profile?.displayName,
      user?.profile?.name,
    ].filter((value) => typeof value === 'string' && value.trim().length > 0);

    if (candidates.length > 0) {
      return String(candidates[0]);
    }

    const userId = this.getUserId();
    return userId ? String(userId) : null;
  }

  /**
   * 토큰 변경 리스너 등록
   */
  addTokenChangeListener(listener: (token: string | null) => void): void {
    this.listeners.push(listener);
  }

  /**
   * 토큰 변경 리스너 제거
   */
  removeTokenChangeListener(listener: (token: string | null) => void): void {
    this.listeners = this.listeners.filter(l => l !== listener);
  }

  /**
   * 모든 리스너에게 토큰 변경 알림
   */
  private notifyListeners(token: string | null): void {
    this.listeners.forEach(listener => {
      try {
        listener(token);
      } catch (error) {
        console.error('[TokenManager] Error in token change listener:', error);
      }
    });
  }

  /**
   * 토큰 클리어 (사용자 정보와 ID 토큰도 함께 삭제)
   */
  clearToken(): void {
    this.stopAutoRefresh();
    this.setToken(null);
    this.setRefreshToken(null);
    this.currentExpiresAt = null;
    localStorage.removeItem(this.storageKeys.tokenExpiresAtKey);

    // 중요: 토큰 클리어 시 사용자 정보와 ID 토큰도 함께 삭제
    // 이렇게 하지 않으면 auth_user는 남아있는데 auth_token은 없는 불일치 상태가 발생
    localStorage.removeItem(this.storageKeys.userKey);
    localStorage.removeItem(this.storageKeys.idTokenKey);

    console.log('[TokenManager] 토큰, 사용자 정보, ID 토큰 모두 클리어됨');
  }

  /**
   * Auth Server URL 설정
   */
  setAuthServerUrl(url: string): void {
    this.authServerUrl = url;
  }

  /**
   * 주기적 토큰 갱신 시작 (5분마다 토큰 상태 확인 및 갱신)
   */
  startAutoRefresh(intervalMinutes: number = 5): void {
    if (this.refreshTimer) {
      clearInterval(this.refreshTimer);
    }

    if (!this.currentRefreshToken) {
      console.warn('[TokenManager] Refresh token not available. Cannot start auto refresh.');
      return;
    }

    console.log(`[TokenManager] Starting auto refresh every ${intervalMinutes} minutes`);

    this.refreshTimer = setInterval(async () => {
      if (this.currentRefreshToken && !this.isRefreshing) {
        console.log('[TokenManager] Auto refresh triggered');
        await this.refreshTokenIfNeeded();
      }
    }, intervalMinutes * 60 * 1000);
  }

  /**
   * 주기적 토큰 갱신 중지
   */
  stopAutoRefresh(): void {
    if (this.refreshTimer) {
      clearInterval(this.refreshTimer);
      this.refreshTimer = null;
      console.log('[TokenManager] Auto refresh stopped');
    }
  }

  /**
   * 토큰 상태 디버깅 정보 출력 (브라우저 콘솔용)
   */
  debugTokenStatus(): void {
    const now = Date.now();
    const accessToken = this.getToken();
    const refreshToken = this.getRefreshToken();
    const expiresAt = this.currentExpiresAt;

    console.group('🔐 [TokenManager] Debug Status');
    console.log('현재 시간:', new Date().toISOString());
    console.log('Access Token 존재:', !!accessToken, accessToken ? `(길이: ${accessToken.length})` : '');
    console.log('Refresh Token 존재:', !!refreshToken, refreshToken ? `(길이: ${refreshToken.length})` : '');
    console.log('Token Type:', this.getTokenType());

    if (expiresAt) {
      const timeUntilExpiry = expiresAt - now;
      const minutesUntilExpiry = Math.floor(timeUntilExpiry / 1000 / 60);
      console.log('만료 시간:', new Date(expiresAt).toISOString());
      console.log('만료까지 남은 시간:', minutesUntilExpiry, '분');
      console.log('만료 상태:', this.isTokenExpired() ? '만료됨' : '유효함');
    } else {
      console.log('만료 시간 정보 없음');
    }

    console.log('자동 갱신 활성:', !!this.refreshTimer);
    console.log('현재 갱신 중:', this.isRefreshing);
    console.groupEnd();
  }

  /**
   * 필요 시 토큰 갱신 (수동 호출용)
   */
  async refreshTokenIfNeeded(): Promise<boolean> {
    if (!this.currentRefreshToken) {
      console.warn('[TokenManager] No refresh token available');
      return false;
    }

    if (this.isRefreshing && this.pendingRefreshPromise) {
      console.log('[TokenManager] Refresh already in progress, waiting...');
      return this.pendingRefreshPromise;
    }

    this.isRefreshing = true;

    const refreshPromise = (async (): Promise<boolean> => {
      try {
        console.log('[TokenManager] Attempting token refresh...');

        const result = await handleRefreshTokenFlow(
          this.currentRefreshToken!,
          this.authServerUrl,
          localStorage.getItem(this.storageKeys.tokenTypeKey) || 'Bearer'
        );

        if (result.success && result.accessToken) {
          console.log('[TokenManager] Token refresh successful');

          // 새 토큰으로 업데이트
          this.setToken(result.accessToken, result.tokenType || 'Bearer');

          // 새 refresh token이 있다면 업데이트
          if (result.refreshToken) {
            this.setRefreshToken(result.refreshToken);
          }

          return true;
        } else {
          console.error('[TokenManager] Token refresh failed:', result.error);

          // refresh token이 만료된 경우 로그아웃 처리
          if (result.error === 'invalid_grant') {
            console.log('[TokenManager] Refresh token expired, clearing tokens');
            this.clearToken();
          }

          return false;
        }
      } catch (error) {
        console.error('[TokenManager] Token refresh error:', error);
        return false;
      }
    })();

    this.pendingRefreshPromise = refreshPromise;

    const success = await refreshPromise;

    this.isRefreshing = false;
    this.pendingRefreshPromise = null;

    return success;
  }
}

// 싱글톤 인스턴스 export
export const tokenManager = TokenManager.getInstance();
